import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  VerifiedUserOutlined as FairIcon,
  AccountBalanceWalletOutlined as PayoutIcon,
  OpenInNew as ExplorerIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";

const HIGHLIGHTS = [
  {
    icon: FairIcon,
    title: "Chainlink VRF Randomness",
    desc: "Every winning number is generated via decentralized Verifiable Random Function (VRF), making the draw mathematically tamper-proof.",
  },
  {
    icon: PayoutIcon,
    title: "Instant Wallet Payouts",
    desc: "Winnings are transferred directly and automatically to the winner's Web3 address by the smart contract upon draw resolution.",
  },
  {
    icon: ExplorerIcon,
    title: "Publicly Auditable",
    desc: "Every draw, ticket purchase, and winner transaction is permanently recorded on the blockchain and inspectable on public block explorers.",
  },
];

const FAQS = [
  {
    q: "How do I know the results are fair and not manipulated?",
    a: "Autobet smart contracts integrate Chainlink VRF (Verifiable Random Function). Neither the platform admins, creators, nor players can predict or alter the random seed used to select winning tickets.",
  },
  {
    q: "Do I need to manually claim my winnings?",
    a: "No! Autobet lotteries feature automated non-custodial payouts. The smart contract automatically transfers the winning prize pool directly to your connected Web3 wallet upon draw conclusion.",
  },
  {
    q: "Can I verify my winning transaction on the block explorer?",
    a: "Yes. Every result card includes an external link to view the winner address and smart contract transactions directly on the blockchain explorer.",
  },
];

const ResultsTransparencyInfo = () => {
  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      {/* 3 Pillars Overview */}
      <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HelpIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}
            >
              Trust, Fairness & On-Chain Transparency
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500] }}>
              How decentralized draw resolution and payouts work on Autobet
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {HIGHLIGHTS.map((item, idx) => {
            const PillarIcon = item.icon;
            return (
              <Grid item xs={12} md={4} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: colors.slate[50],
                    borderRadius: borderRadiusPx.lg,
                    border: `1px solid ${colors.slate[200]}`,
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[100],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    <PillarIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.slate[900], mb: 0.5 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[600], fontSize: "0.82rem", lineHeight: 1.5 }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Frequently Asked Questions */}
      <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: colors.slate[900], mb: 2, fontSize: "1.05rem" }}
        >
          Frequently Asked Questions About Results
        </Typography>

        <Stack spacing={1.5}>
          {FAQS.map((faq, index) => (
            <Accordion
              key={index}
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${colors.slate[200]}`,
                borderRadius: `${borderRadiusPx.md} !important`,
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: colors.slate[500] }} />}
                sx={{
                  bgcolor: colors.slate[50],
                  px: 2,
                  py: 0.5,
                  "& .MuiAccordionSummary-content": { my: 1 },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: colors.slate[800] }}
                >
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1.5, bgcolor: colors.dashboard.cardBg }}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                >
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default ResultsTransparencyInfo;
